import React, { FC, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import classNames from 'classnames';

import { FeatureFlag } from 'app/api/services/site-configs/types';
import type { ReduxState } from 'app/redux/types';

import { Balance } from 'app/design-system/components/Balance';
import CurrencySymbol from 'app/design-system/components/CurrencySymbol';
import { Icon } from 'app/design-system/components/Icon';
import { ICON_NAME_WALLET_TOP_UP } from 'app/design-system/constants/IconNames';
import { useTranslation } from 'app/lang';
import { isAccountBalance, selectClientInfoData } from 'app/lib/account/selectors';
import { useCheckFeatureFlagValue, useFeatureFlag } from 'app/lib/feature-flags';
import { ROUTE_PATH } from 'app/lib/routing/constants';
import { isUsernameHidden } from 'app/lib/site-configs/selectors';
import selectCurrency from 'app/redux/selectors/сurrency';

import dictionary from './AccountBalance.dictionary.json';

import styles from './AccountBalance.module.scss';

export const AccountBalance: FC = () => {
  const { t, lang } = useTranslation();
  const hasClientInfo = useSelector((state: ReduxState) => selectClientInfoData(state) !== null);
  const currency = useSelector(selectCurrency);
  const isAccountDetailsHidden = useSelector(isUsernameHidden);
  const isBalance = useSelector(isAccountBalance);
  const isExpBalanceIconColor = useFeatureFlag(FeatureFlag.EXP_BALANCE_ICON_COLOR);
  const isAccentIcon = useCheckFeatureFlagValue(
    FeatureFlag.EXP_BALANCE_ICON_COLOR,
    'accent',
  );

  const iconClassName = classNames({
    [styles.accentIcon]: isExpBalanceIconColor && isAccentIcon,
  });

  const balanceContent = useMemo(() => {
    if (!isBalance) {
      return (
        <span className={styles.text}>
          {t(dictionary.topUpBalance)}
        </span>
      );
    }
    if (isAccountDetailsHidden) {
      return (
        <p className={styles.hiddenBalance}>
          <span className={styles.hiddenBalanceValue}>*****</span>
          <CurrencySymbol currency={currency} />
        </p>
      );
    }
    return <Balance />;
  }, [isAccountDetailsHidden, isBalance, lang]);

  if (!hasClientInfo) {
    return null;
  }

  return (
    <Link to={ROUTE_PATH.DEPOSIT}
          data-ga-event="depositStart"
          data-ga-event-value="headerClick">
      <div data-test="amount"
           className={styles.container}>
        {balanceContent}

        <Icon className={iconClassName}
              icon={ICON_NAME_WALLET_TOP_UP} />
      </div>
    </Link>
  );
};
